import React, { useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * NewsletterSignup — compact email capture form.
 * Posts to /api/newsletter and shows an inline success state.
 */
export const NewsletterSignup = ({ source = 'footer', className }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || 'Something went wrong');
      setStatus('success');
      setMessage(`We'll write from the studio — thanks, ${email}.`);
      setEmail('');
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  };

  return (
    <div className={cn('w-full', className)} data-testid={`newsletter-${source}`}>
      <p className="text-xs uppercase tracking-[0.18em] text-ink mb-3">Studio Dispatch</p>
      <p className="text-sm text-soft mb-4 leading-relaxed">
        A short letter from the studio when new work drops. No noise.
      </p>

      {status === 'success' ? (
        <div
          className="flex items-center gap-2 text-sm text-terracotta"
          data-testid="newsletter-success"
        >
          <Check className="w-4 h-4" />
          {message}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex items-stretch gap-0 border-b border-ink/30 focus-within:border-ink transition-colors">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@somewhere.com"
            className="flex-1 bg-transparent py-3 text-sm text-ink placeholder:text-soft/60 focus:outline-none"
            data-testid="newsletter-email-input"
            aria-label="Email address"
          />
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="ml-3 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors disabled:opacity-50"
            data-testid="newsletter-submit"
          >
            {status === 'submitting' ? 'Sending' : 'Subscribe'}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </form>
      )}

      {status === 'error' && (
        <p className="mt-2 text-xs text-terracotta" data-testid="newsletter-error">
          {message}
        </p>
      )}
    </div>
  );
};

export default NewsletterSignup;
